/**
 * Makes all properties in T optional recursively
 */
export type DeepPartial<T> = T extends
  | Array<unknown>
  | Date
  | RegExp
  | ((...args: never[]) => unknown)
  ? T
  : T extends object
    ? {
        [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

const keys = <T extends Record<string, unknown>>(obj: T): (keyof T)[] =>
  Object.keys(obj) as (keyof T)[];

const values = <T extends Record<string, unknown>>(obj: T): T[keyof T][] =>
  Object.values(obj) as T[keyof T][];

const pick = <T extends Record<string, unknown>>(
  obj: T,
  ...keys: (keyof T)[]
): T =>
  Object.entries(obj).reduce(
    (acc, [k, v]) => (keys.includes(k) ? { ...acc, [k]: v } : acc),
    {} as T
  );

const without = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  ...keys: K[]
): Omit<T, K> =>
  Object.entries(obj).reduce(
    (acc, [k, v]) => (keys.includes(k as K) ? acc : { ...acc, [k]: v }),
    {} as Omit<T, K>
  );

const fromEntries = <K extends number | string | symbol, V>(
  entries: readonly (readonly [K, V])[]
): Record<K, V> => {
  return Object.fromEntries(entries) as Record<K, V>;
};

const entries = <T extends Record<string, unknown>>(
  obj: T
): [keyof T, T[keyof T]][] => Object.entries(obj) as [keyof T, T[keyof T]][];

export const ObjectUtils = {
  keys,
  values,
  pick,
  without,
  fromEntries,
  entries,
} as const;
